import dayjs from 'dayjs';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import LegacyContractStatusCode from 'models/enums/LegacyContractStatusCode';

type ContractWithLegacyStatus = {
  id?: number;
  legacyId?: number;
  statusCode?: Model.Enum.ContractStatusCode | string;
  expiryDate?: string;
};

export const allowedContractStatusesForClaimCreation = [
  ContractStatusCode.OPEN_SUBMITTED,
  ContractStatusCode.OPEN_DEFERRED,
  ContractStatusCode.OPEN_UNDER_REVIEW,
  ContractStatusCode.OPEN_UNPAID,
  ContractStatusCode.OPEN_WAITING_CONSENT,
  ContractStatusCode.PAID_ACTIVE,
];
export const allowedContractStatusesForClaimCreationWithExpired = [
  ...allowedContractStatusesForClaimCreation,
  ContractStatusCode.PAID_EXPIRED,
];
const allowedContractStatusSet = new Set<string>(allowedContractStatusesForClaimCreationWithExpired);
const legacyToImportedStatusMap: Record<LegacyContractStatusCode, ContractStatusCode> = {
  PAID: ContractStatusCode.PAID_ACTIVE,
  UNPAID: ContractStatusCode.OPEN_UNPAID,
  PENDING: ContractStatusCode.OPEN_UNPAID,
};

export function getLegacyContractStatus(contract?: ContractWithLegacyStatus) {
  const legacyStatusCode = contract?.legacyId && contract?.statusCode;
  if (legacyStatusCode) return legacyStatusCode;

  const statusCode = contract?.statusCode;
  return statusCode && !allowedContractStatusSet.has(statusCode) ? statusCode : undefined;
}

export function canCreateClaimForContractStatus(contract?: ContractWithLegacyStatus) {
  const statusCode = contract?.statusCode;

  if (statusCode && allowedContractStatusSet.has(statusCode)) return true;

  const legacyStatus = getLegacyContractStatus(contract);
  return !!(legacyStatus && legacyToImportedStatusMap[legacyStatus]);
}

export function isClaimContractExpired(contract?: ContractWithLegacyStatus) {
  if (!contract?.expiryDate) return false;

  return contract.statusCode === ContractStatusCode.PAID_EXPIRED || dayjs().isAfter(dayjs(contract.expiryDate));
}

export function isClaimContractExpiredWindowExceeded(contract?: ContractWithLegacyStatus) {
  if (!contract?.expiryDate) return false;

  return dayjs().isAfter(dayjs(contract.expiryDate).add(30, 'days'));
}

export function mapLegacyContractStatusForImport<T extends ContractWithLegacyStatus>(contract?: T): T | undefined {
  if (!contract) return contract;

  const legacyStatus = getLegacyContractStatus(contract);
  const mappedStatus = legacyStatus && legacyToImportedStatusMap[legacyStatus];

  if (!mappedStatus || contract.statusCode === mappedStatus) return contract;

  return {
    ...contract,
    statusCode: mappedStatus,
  } as T;
}
